import { appendVimeoEmbed, createYoutubeEmbed } from "../utils/video-embeds.js";

/** Distance to translate the fixed loader down so its exit aligns with the bottom of `.banner` (not full viewport). */
function getLoaderExitTranslateYPx() {
  const banner = document.querySelector(".banner");
  if (!banner) {
    return window.innerHeight;
  }
  const bottom = banner.getBoundingClientRect().bottom;
  if (!Number.isFinite(bottom) || bottom <= 0) {
    return window.innerHeight;
  }
  return bottom;
}

export const loader = () => {

  const gsap = window.gsap;
  if (!gsap) return;

  const loaderShell = document.querySelector(".loader-shell");
  const loaderEl = document.querySelector(".loader");
  if (!loaderEl) return;

  document.body.classList.add("loader-active");

  const videoWrap = loaderEl.querySelector(".loader__video-wrap");
  const title = loaderEl.querySelector(".loader__title.type-text");
  const titleInner = loaderEl.querySelector(".type-text__inner");
  const subtitleWrap = loaderEl.querySelector(".loader__subtitle-wrap");

  if (!videoWrap || !title || !titleInner || !subtitleWrap) return;

  const videoType = (videoWrap.getAttribute("data-video-type") || "mp4").toLowerCase();
  const videoSrc = videoWrap.getAttribute("data-video-src") || "";
  const videoId = (videoWrap.getAttribute("data-video-id") || "").trim();

  const embedClass = "loader__video loader__video--embed";

  const injectVideo = () => {
    if (videoType === "mp4" && videoSrc) {
      const el = document.createElement("video");
      el.className = "loader__video";
      el.src = videoSrc;
      el.muted = true;
      el.loop = true;
      el.playsInline = true;
      el.setAttribute("playsinline", "");
      el.autoplay = true;
      videoWrap.appendChild(el);
      return Promise.resolve();
    }
    if (videoType === "youtube" && videoId) {
      return createYoutubeEmbed(videoWrap, videoId, embedClass).then(() => {});
    }
    if (videoType === "vimeo" && videoId) {
      appendVimeoEmbed(videoWrap, videoId, embedClass);
      return Promise.resolve();
    }
    return Promise.resolve();
  };

  injectVideo().then(() => {
    // --- Typewriter: one span per character, words grouped so long text wraps between words (not mid-letter) ---
    const text = (titleInner.textContent || "Introduction ...").trim();
    const fragment = document.createDocumentFragment();
    const segments = text.split(/(\s+)/);

    for (const segment of segments) {
      if (!segment) continue;
      if (/^\s+$/.test(segment)) {
        for (let j = 0; j < segment.length; j++) {
          const span = document.createElement("span");
          span.className = "type-text__char";
          span.textContent = segment[j];
          fragment.appendChild(span);
        }
      } else {
        const wordSpan = document.createElement("span");
        wordSpan.className = "type-text__word";
        for (let i = 0; i < segment.length; i++) {
          const span = document.createElement("span");
          span.className = "type-text__char";
          span.textContent = segment[i];
          wordSpan.appendChild(span);
        }
        fragment.appendChild(wordSpan);
      }
    }

    titleInner.textContent = "";
    titleInner.appendChild(fragment);
    const chars = titleInner.querySelectorAll(".type-text__char");

    let contentRevealDone = false;
    let exitSlidePx = 0;
    const fadeTarget = loaderShell || loaderEl;

    // --- Timeline: single timeline, kill on complete to free memory ---
    const tl = gsap.timeline({
      defaults: { ease: "power2.out" },
      overwrite: "auto",
    });

    tl.set(title, { opacity: 1, visibility: "visible" })
      .to(chars, {
        opacity: 1,
        duration: 0.01,
        stagger: 0.1,
        ease: "none",
      })
      .set(videoWrap, { visibility: "visible", opacity: 0 })
      .set(subtitleWrap, { visibility: "visible", opacity: 0 })
      .to(videoWrap, { opacity: 1, duration: 1.25, ease: "power1.inOut" })
      .to(title, { opacity: 0, duration: 1, ease: "power2.inOut" }, "<")
      .add(() => title.remove())
      .to(subtitleWrap, { opacity: 1, duration: 0.2, ease: "power1.inOut" }, "<")
      .to({}, { duration: 2 })
      .to(subtitleWrap, { opacity: 0, duration: 0.6, ease: "power1.inOut" })
      .call(() => {
        exitSlidePx = getLoaderExitTranslateYPx();
        if (loaderShell) {
          gsap.set(loaderShell, { height: exitSlidePx });
        }
      })
      .to(loaderEl, {
        y: () => (loaderShell ? exitSlidePx : window.innerHeight),
        duration: 2,
        ease: "power2.inOut",
        onStart: () => {
          document.body.classList.remove("loader-active");
        },
        onUpdate: function () {
          if (contentRevealDone || this.progress() < 0.5) return;
          contentRevealDone = true;
          document.dispatchEvent(new CustomEvent("loaderContentReveal"));
        },
      })
      .to(
        fadeTarget,
        {
          opacity: 0,
          duration: 0.38,
          ease: "power2.inOut",
          onComplete: () => {
            tl.kill();
            fadeTarget.remove();
            document.dispatchEvent(new CustomEvent("loaderComplete"));
          },
        },
        ">"
      );
  });
};
